import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';
import {
  getCode,
  getCodes,
  getSubcode,
  getSubcodes,
  createCode,
  createSubcode,
  updateCode,
  updateSubcode,
  updateSubcodeSeq,
} from '../api/treeCodes.api';
import {
  ITreeCode,
  ITreeSubcode,
  IGetTreeCode,
  IGetTreeCodes,
  IGetTreeSubcode,
  IGetTreeSubcodes,
  ITreeCodeReq,
  ITreeCodeSeqReq,
  IAddTreeSubCodeReq,
  ITreeSubCodeReq,
} from '../models/treeCodes.model';

interface ITreeCodeState {
  treeCode?: ITreeCode;
  treeCodes?: ITreeCode[];
  subcode?: ITreeSubcode;
  subcodes?: ITreeSubcode[];
  isLoading: boolean;
  error?: AxiosError | string;
}

const initialState: ITreeCodeState = {
  treeCode: undefined,
  treeCodes: [],
  isLoading: false,
};

const codeSlicer = createSlice({
  name: 'treeCode',
  initialState,
  reducers: {
    getCodeStart: (state: ITreeCodeState) => {
      state.treeCode = undefined;
      state.isLoading = true;
      state.error = undefined;
    },
    getCodeSuccess: (state: ITreeCodeState, action: PayloadAction<IGetTreeCode>) => {
      state.treeCode = action.payload.data;
      state.isLoading = false;
      state.error = undefined;
    },
    getCodeFailure: (
      state: ITreeCodeState,
      action: PayloadAction<AxiosError | string>,
    ) => {
      state.treeCode = undefined;
      state.isLoading = false;
      state.error = action.payload;
    },
    getCodesStart: (state: ITreeCodeState) => {
      state.treeCodes = [];
      state.isLoading = true;
      state.error = undefined;
    },
    getCodesSuccess: (state: ITreeCodeState, action: PayloadAction<IGetTreeCodes>) => {
      state.treeCodes = action.payload.data;
      state.isLoading = false;
      state.error = undefined;
    },
    getCodesFailure: (
      state: ITreeCodeState,
      action: PayloadAction<AxiosError | string>,
    ) => {
      state.treeCodes = [];
      state.isLoading = false;
      state.error = action.payload;
    },
    getSubcodeStart: (state: ITreeCodeState) => {
      state.subcode = undefined;
      state.isLoading = true;
      state.error = undefined;
    },
    getSubcodeSuccess: (
      state: ITreeCodeState,
      action: PayloadAction<IGetTreeSubcode>,
    ) => {
      state.subcode = action.payload.data;
      state.isLoading = false;
      state.error = undefined;
    },
    getSubcodeFailure: (
      state: ITreeCodeState,
      action: PayloadAction<AxiosError | string>,
    ) => {
      state.subcode = undefined;
      state.isLoading = false;
      state.error = action.payload;
    },
    getSubcodesStart: (state: ITreeCodeState) => {
      state.subcodes = [];
      state.isLoading = true;
      state.error = undefined;
    },
    getSubcodesSuccess: (
      state: ITreeCodeState,
      action: PayloadAction<IGetTreeSubcodes>,
    ) => {
      state.subcodes = action.payload.data;
      state.isLoading = false;
      state.error = undefined;
    },
    getSubcodesFailure: (
      state: ITreeCodeState,
      action: PayloadAction<AxiosError | string>,
    ) => {
      state.subcodes = [];
      state.isLoading = false;
      state.error = action.payload;
    },
    requestStart: (state: ITreeCodeState) => {
      state.isLoading = true;
      state.error = undefined;
    },
    requestFailure: (
      state: ITreeCodeState,
      action: PayloadAction<AxiosError | string>,
    ) => {
      state.isLoading = false;
      state.error = action.payload;
    },
    createSuccess: (state: ITreeCodeState) => {
      state.isLoading = false;
      state.error = undefined;
    },
    updateSuccess: (state: ITreeCodeState) => {
      state.isLoading = false;
      state.error = undefined;
    },
  },
});

export const {
  getCodeStart,
  getCodeSuccess,
  getCodeFailure,
  getCodesStart,
  getCodesSuccess,
  getCodesFailure,
  getSubcodeStart,
  getSubcodeSuccess,
  getSubcodeFailure,
  getSubcodesStart,
  getSubcodesSuccess,
  getSubcodesFailure,
  requestStart,
  requestFailure,
  createSuccess,
  updateSuccess,
} = codeSlicer.actions;

export default codeSlicer.reducer;

export const doGetCode =
  (id: string): any =>
  async (dispatch: any) => {
    try {
      dispatch(getCodeStart());
      const response = await getCode(id);
      dispatch(getCodeSuccess(response.data));
      return response;
    } catch (e: any) {
      dispatch(getCodeFailure(e.response?.data?.data || e.message));
      return e.response;
    }
  };

export const doGetCodes = (): any => async (dispatch: any) => {
  try {
    dispatch(getCodesStart());
    const response = await getCodes();
    dispatch(getCodesSuccess(response.data));
    return response;
  } catch (e: any) {
    dispatch(getCodesFailure(e.response?.data?.data || e.message));
    return e.response;
  }
};

export const doGetTreeSubcode =
  (code: string, id: string): any =>
  async (dispatch: any) => {
    try {
      dispatch(getSubcodeStart());
      const response = await getSubcode(code, id);
      dispatch(getSubcodeSuccess(response.data));
      return response;
    } catch (e: any) {
      dispatch(getSubcodeFailure(e.response?.data?.data || e.message));
      return e.response;
    }
  };

export const doGetTreeSubcodes =
  (code: string, data: ITreeSubCodeReq): any =>
  async (dispatch: any) => {
    try {
      dispatch(getSubcodesStart());
      const response = await getSubcodes(code, data);
      dispatch(getSubcodesSuccess(response.data));
      return response;
    } catch (e: any) {
      dispatch(getSubcodesFailure(e.response?.data?.data || e.message));
      return e.response;
    }
  };

export const doCreateCode =
  (data: ITreeCodeReq): any =>
  async (dispatch: any) => {
    try {
      dispatch(requestStart());
      const response = await createCode(data);
      dispatch(createSuccess());
      return response;
    } catch (e: any) {
      dispatch(requestFailure(e.response?.data?.data || e.message));
      return e.response;
    }
  };

export const doCreateTreeSubcode =
  (code: string, data: IAddTreeSubCodeReq): any =>
  async (dispatch: any) => {
    try {
      dispatch(requestStart());
      const response = await createSubcode(code, data);
      dispatch(createSuccess());
      return response;
    } catch (e: any) {
      dispatch(requestFailure(e.response?.data?.data || e.message));
      return e.response;
    }
  };

export const doUpdateCode =
  (code: string, data: ITreeCodeReq): any =>
  async (dispatch: any) => {
    try {
      dispatch(requestStart());
      const response = await updateCode(code, data);
      dispatch(updateSuccess());
      return response;
    } catch (e: any) {
      dispatch(requestFailure(e.response?.data?.data || e.message));
      return e.response;
    }
  };

export const doUpdateTreeSubcode =
  (code: string, detailCode: number, data: IAddTreeSubCodeReq): any =>
  async (dispatch: any) => {
    try {
      dispatch(requestStart());
      const response = await updateSubcode(code, detailCode, data);
      dispatch(updateSuccess());
      return response;
    } catch (e: any) {
      dispatch(requestFailure(e.response?.data?.data || e.message));
      return e.response;
    }
  };

export const doUpdateSubcodeSeq =
  (code: string, data: ITreeCodeSeqReq[]): any =>
  async (dispatch: any) => {
    try {
      dispatch(requestStart());
      const response = await updateSubcodeSeq(code, data);
      dispatch(updateSuccess());
      return response;
    } catch (e: any) {
      dispatch(requestFailure(e.response?.data?.data || e.message));
      return e.response;
    }
  };
